import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Lock, Rocket } from "lucide-react";
import axios from "axios";
import { LogoMark } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "gelvaro_site_access";

export const SiteGate = ({ children }) => {
  const [state, setState] = useState("checking"); // checking | locked | open
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY) === "granted") {
      setState("open");
      return;
    }
    axios
      .get(`${API}/site-access/status`)
      .then((res) => setState(res.data.gate_enabled ? "locked" : "open"))
      .catch(() => setState("open"));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await axios.post(`${API}/site-access`, { code: code.trim() });
      localStorage.setItem(STORAGE_KEY, "granted");
      setState("open");
    } catch (err) {
      setError(err.response?.status === 403 ? "Onjuiste toegangscode. Probeer het opnieuw." : "Er ging iets mis. Probeer het later opnieuw.");
    } finally {
      setBusy(false);
    }
  };

  if (state === "checking") {
    return (
      <div className="min-h-screen bg-[#090D16] flex items-center justify-center" data-testid="site-gate-loading">
        <Loader2 className="w-6 h-6 text-[#00FF66] animate-spin" />
      </div>
    );
  }

  if (state === "locked") {
    return (
      <div data-testid="site-gate" className="relative min-h-screen bg-[#090D16] text-slate-100 flex items-center justify-center overflow-hidden px-4">
        <div className="noise-overlay" />
        <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#00FF66]/10 blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 -right-40 w-[520px] h-[520px] rounded-full bg-[#FF087B]/10 blur-[130px] pointer-events-none" />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 w-full max-w-md text-center"
        >
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="flex justify-center mb-7"
          >
            <LogoMark className="w-20 h-20" />
          </motion.div>

          <p className="inline-flex items-center gap-2 bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/30 px-4 py-1.5 rounded-full text-xs font-mono-brand uppercase tracking-widest mb-6">
            <Rocket className="w-3.5 h-3.5" /> Binnenkort open
          </p>

          <h1 className="font-display text-5xl sm:text-6xl uppercase tracking-wide leading-none">
            Gelvaro <span className="text-gradient-splat">laadt op</span>
          </h1>
          <p className="mt-4 text-slate-400 leading-relaxed">
            We geven de laatste splats de finishing touch. Heb je een toegangscode? Vul hem in en kom als eerste binnen.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-3" data-testid="site-gate-form">
            <div className="relative">
              <Lock className="w-4 h-4 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="site-gate-input"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Toegangscode"
                className="w-full bg-slate-900/70 border border-slate-700 focus:border-[#00FF66] focus:ring-1 focus:ring-[#00FF66]/40 rounded-full pl-11 pr-4 py-4 text-sm text-white placeholder:text-slate-500 outline-none transition-colors text-center tracking-widest font-mono-brand uppercase"
              />
            </div>
            {error && <p className="text-sm text-[#FF087B] font-semibold" data-testid="site-gate-error">{error}</p>}
            <button
              type="submit"
              data-testid="site-gate-submit"
              disabled={busy || !code.trim()}
              className="w-full bg-gradient-to-r from-[#00FF66] to-[#00E5FF] text-slate-950 font-extrabold uppercase tracking-wider py-4 rounded-full glow-lime hover:scale-[1.01] active:scale-95 transition-all disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              Toegang
            </button>
          </form>

          <p className="mt-8 text-xs text-slate-600 font-mono-brand uppercase tracking-widest">Laden • Richten • Splat</p>
        </motion.div>
      </div>
    );
  }

  return children;
};
